import { useNavigate } from 'react-router';
import { HStack, Spinner, Text } from '@chakra-ui/react';
import type { GroupInfo, GroupMemberJoinMode } from '@halo/chat-sdk-react';
import { GroupJoinStatusEnum, GroupStatusEnum, useAppStore } from '@halo/chat-sdk-react';
import { tools } from '@halo/ui-chat-react';
import Paths from '@/configs/router/paths';
import { useActionsStore } from '@/store/actions';

enum StatusEnum {
  Join = 0,
  Pending = 1,
  Messages = 2,
}

export default function ReceiverActions({
  group,
  onClose,
}: {
  group: GroupInfo & { mode: GroupMemberJoinMode };
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const { joinSession } = useAppStore();
  const { setGroupOpen } = useActionsStore();
  const navigate = useNavigate();

  const [status, setStatus] = useState<number>(StatusEnum.Join);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (group.join_status === GroupJoinStatusEnum.Pending) {
      setStatus(StatusEnum.Pending);
      return;
    }
    if (group.join_status === GroupJoinStatusEnum.Joined) {
      setStatus(StatusEnum.Messages);
      return;
    }
    setStatus(StatusEnum.Join);
  }, [group.join_status]);

  const navData: Array<{
    key: string;
    text: string;
    disabled: boolean;
    ban?: boolean;
    onclick?: () => void;
  }> = [
    {
      key: 'Join',
      text: t('buttons.join'),
      disabled: status !== StatusEnum.Join,
      onclick: async () => {
        if (loading) return;
        try {
          if (group.status === GroupStatusEnum.BAN) {
            tools.toaster.error({
              title: t('tips.groupAbnormal'),
            });
            return;
          }
          setLoading(true);
          const { status } = await joinSession(group.gid, group.mode);
          if (status === 1) {
            setStatus(StatusEnum.Messages);
            navigate(Paths.MESSAGES_CHATS + '/' + group.gid);
            setGroupOpen(false);
            onClose?.();
          }
          if (status === 2) {
            setStatus(StatusEnum.Pending);
          }
        } finally {
          setLoading(false);
        }
      },
    },
    {
      key: 'Pending',
      text: t('buttons.pending'),
      disabled: status !== StatusEnum.Pending,
      ban: true,
    },
    {
      key: 'Messages',
      text: t('buttons.messages'),
      disabled: status !== StatusEnum.Messages,
      onclick: async () => {
        navigate(Paths.MESSAGES_CHATS + '/' + group.gid);
        setGroupOpen(false);
        onClose?.();
      },
    },
  ];

  return (
    <HStack w="full" justifyContent="space-between" gap="2.5">
      {navData
        .filter((item) => !item.disabled)
        .map((item) => {
          const Item = (
            <>
              <Text fontSize="md" fontWeight="semibold">
                {item.text}
              </Text>
            </>
          );

          return (
            <HStack
              bg={item.ban ? 'grey.80' : 'brand'}
              flex={1}
              rounded="xl"
              py="4"
              justifyContent="center"
              gap="1"
              key={item.key}
              onClick={item?.onclick}
              cursor={item.ban ? 'not-allowed' : 'pointer'}
              color={item.ban ? 'grey.92' : 'white'}
            >
              {loading && <Spinner />}
              {Item}
            </HStack>
          );
        })}
    </HStack>
  );
}
